import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useToast } from '../Toast';
import * as api from '../../services/api';

const SecuritySettings: React.FC = () => {
  const { user } = useAuth();
  const { showToast } = useToast();
  const [sessionTimeout, setSessionTimeout] = useState('60');
  const [requireReauth, setRequireReauth] = useState(true);
  const [ipAllowlistEnabled, setIpAllowlistEnabled] = useState(false);
  const [ipInput, setIpInput] = useState('');
  const [ipList, setIpList] = useState<string[]>([]);
  const [webhookUrl, setWebhookUrl] = useState('');
  const [webhooks, setWebhooks] = useState<string[]>([]);
  const [saving, setSaving] = useState(false);
  const [saveStatus, setSaveStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [auditTrail, setAuditTrail] = useState<any[]>([]);
  const [loadingAudit, setLoadingAudit] = useState(true);

  // Fetch audit summary on mount
  useEffect(() => {
    const fetchAudit = async () => {
      try {
        const data = await api.getAuditSummary();
        // Map summary to trail entries
        setAuditTrail([
          { action: `${data.total_scans || 0} total scans completed`, user: 'System', time: data.last_scan_at ? new Date(data.last_scan_at).toLocaleString() : 'Never', type: 'audit' },
          { action: `${data.completed || 0} successful, ${data.failed || 0} failed`, user: 'System', time: 'Summary', type: 'agent' },
          { action: `Average compliance score: ${data.avg_score || 'N/A'}%`, user: 'System', time: 'Current', type: 'settings' },
        ]);
      } catch {
        setAuditTrail([{ action: 'Audit trail unavailable — API not reachable', user: 'System', time: 'Now', type: 'security' }]);
      } finally {
        setLoadingAudit(false);
      }
    };
    fetchAudit();
  }, []);

  const handleSaveSettings = async () => {
    if (!user?.tenantId) return;
    setSaving(true);
    setSaveStatus('idle');
    try {
      await api.updateTenant(user.tenantId, {
        settings: { sessionTimeout: Number(sessionTimeout), requireReauth, ipAllowlistEnabled, ipList, webhooks }
      });
      setSaveStatus('success');
      showToast('Security settings saved successfully', 'success');
      setTimeout(() => setSaveStatus('idle'), 3000);
    } catch {
      setSaveStatus('error');
      showToast('Failed to save settings', 'error');
    } finally {
      setSaving(false);
    }
  };

  const addIp = () => {
    if (ipInput && /^(\d{1,3}\.){3}\d{1,3}(\/\d{1,2})?$/.test(ipInput)) {
      setIpList(prev => [...prev, ipInput]);
      setIpInput('');
    }
  };

  const addWebhook = () => {
    if (webhookUrl && webhookUrl.startsWith('https://')) {
      setWebhooks(prev => [...prev, webhookUrl]);
      setWebhookUrl('');
    }
  };

  return (
    <div className="space-y-6">
      {/* Save bar */}
      <div className="flex items-center justify-between">
        <div>
          {saveStatus === 'success' && <span className="text-xs text-anchor-teal">Settings saved</span>}
          {saveStatus === 'error' && <span className="text-xs text-red-400">Failed to save — API may be unavailable</span>}
        </div>
        <button onClick={handleSaveSettings} disabled={saving}
          className="py-2 px-4 bg-anchor-blue border-none rounded-lg text-white text-sm cursor-pointer hover:bg-[#3636DD] transition-colors disabled:opacity-50">
          {saving ? 'Saving...' : 'Save Settings'}
        </button>
      </div>

      {/* Session Configuration */}
      <div className="bg-white/[0.02] border border-white/[0.06] rounded-xl p-6">
        <h3 className="text-lg font-medium text-white mb-5">Session Management</h3>
        <div className="space-y-4">
          <div className="flex items-center justify-between py-3 border-b border-white/[0.04]">
            <div>
              <div className="text-sm text-white/80">Session Timeout</div>
              <div className="text-xs text-white/40 mt-0.5">Automatically log out after period of inactivity</div>
            </div>
            <select value={sessionTimeout} onChange={e => setSessionTimeout(e.target.value)}
              className="py-2 px-3 bg-white/[0.03] border border-white/[0.08] rounded-lg text-sm text-white/70 focus:outline-none focus:border-anchor-blue/50">
              <option value="30">30 minutes</option>
              <option value="60">1 hour</option>
              <option value="120">2 hours</option>
              <option value="480">8 hours</option>
            </select>
          </div>
          <div className="flex items-center justify-between py-3">
            <div>
              <div className="text-sm text-white/80">Require re-authentication</div>
              <div className="text-xs text-white/40 mt-0.5">Require login for sensitive operations</div>
            </div>
            <label className="relative inline-flex items-center cursor-pointer">
              <input type="checkbox" checked={requireReauth} onChange={e => setRequireReauth(e.target.checked)} className="sr-only peer" />
              <div className="w-9 h-5 bg-white/10 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-anchor-blue"></div>
            </label>
          </div>
        </div>
      </div>

      {/* IP Allowlist */}
      <div className="bg-white/[0.02] border border-white/[0.06] rounded-xl p-6">
        <div className="flex items-center justify-between mb-5">
          <div>
            <h3 className="text-lg font-medium text-white">IP Allowlist</h3>
            <p className="text-sm text-white/40 mt-0.5">Restrict agent connections to specific IP ranges</p>
          </div>
          <label className="relative inline-flex items-center cursor-pointer">
            <input type="checkbox" checked={ipAllowlistEnabled} onChange={e => setIpAllowlistEnabled(e.target.checked)} className="sr-only peer" />
            <div className="w-9 h-5 bg-white/10 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-anchor-blue"></div>
          </label>
        </div>
        <div className="flex gap-3 mb-4">
          <input type="text" value={ipInput} onChange={e => setIpInput(e.target.value)} placeholder="e.g. 10.0.0.0/24"
            className="flex-1 py-2.5 px-4 bg-white/[0.03] border border-white/[0.08] rounded-lg text-sm text-white placeholder-white/30 focus:outline-none focus:border-anchor-blue/50 font-mono" />
          <button onClick={addIp} className="py-2.5 px-5 bg-white/[0.04] border border-white/[0.08] rounded-lg text-sm text-white/60 hover:text-white hover:bg-white/[0.06] transition-colors">
            Add Range
          </button>
        </div>
        {ipList.length > 0 ? (
          <div className="space-y-1">
            {ipList.map((ip, i) => (
              <div key={i} className="flex items-center justify-between py-1.5 px-3 bg-white/[0.01] border border-white/[0.03] rounded text-xs">
                <span className="text-white/70 font-mono">{ip}</span>
                <button onClick={() => setIpList(prev => prev.filter((_, idx) => idx !== i))} className="text-red-400 hover:text-red-300">Remove</button>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-xs text-white/30">No IP restrictions configured.</div>
        )}
      </div>

      {/* Audit Trail */}
      <div className="bg-white/[0.02] border border-white/[0.06] rounded-xl p-6">
        <h3 className="text-lg font-medium text-white mb-5">Audit Trail</h3>
        {loadingAudit ? (
          <div className="flex items-center justify-center py-4">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-anchor-blue"></div>
          </div>
        ) : (
          <div className="space-y-2">
            {auditTrail.map((entry, i) => (
              <div key={i} className="flex items-center justify-between py-2.5 px-3 bg-white/[0.01] border border-white/[0.03] rounded-lg">
                <div className="flex items-center gap-3">
                  <div className={`w-1.5 h-1.5 rounded-full ${
                    entry.type === 'security' ? 'bg-amber-400' :
                    entry.type === 'agent' ? 'bg-anchor-teal' :
                    entry.type === 'audit' ? 'bg-anchor-blue' : 'bg-white/20'
                  }`} />
                  <span className="text-sm text-white/70">{entry.action}</span>
                </div>
                <div className="flex items-center gap-4">
                  <span className="text-xs text-white/30 font-mono">{entry.user}</span>
                  <span className="text-xs text-white/20">{entry.time}</span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Webhooks */}
      <div className="bg-white/[0.02] border border-white/[0.06] rounded-xl p-6">
        <h3 className="text-lg font-medium text-white mb-2">Webhooks & Integrations</h3>
        <p className="text-sm text-white/40 mb-4">Send security alerts to external systems</p>
        <div className="flex gap-3">
          <input type="url" value={webhookUrl} onChange={e => setWebhookUrl(e.target.value)} placeholder="https://hooks.slack.com/services/..."
            className="flex-1 py-2.5 px-4 bg-white/[0.03] border border-white/[0.08] rounded-lg text-sm text-white placeholder-white/30 focus:outline-none focus:border-anchor-blue/50 font-mono" />
          <button onClick={addWebhook} className="py-2.5 px-5 bg-white/[0.04] border border-white/[0.08] rounded-lg text-sm text-white/60 hover:text-white hover:bg-white/[0.06] transition-colors">
            Add Webhook
          </button>
        </div>
        {webhooks.length > 0 ? (
          <div className="mt-3 space-y-1">
            {webhooks.map((url, i) => (
              <div key={i} className="flex items-center justify-between py-1.5 px-3 bg-white/[0.01] border border-white/[0.03] rounded text-xs">
                <span className="text-white/70 font-mono truncate">{url}</span>
                <button onClick={() => setWebhooks(prev => prev.filter((_, idx) => idx !== i))} className="text-red-400 hover:text-red-300 ml-2">Remove</button>
              </div>
            ))}
          </div>
        ) : (
          <div className="mt-3 text-xs text-white/30">No webhooks configured.</div>
        )}
      </div>
    </div>
  );
};

export default SecuritySettings;
